/**
 * WhatsApp Notifications & OTP card — host configures:
 *   • whether guests must complete a WhatsApp OTP before requesting a song
 *   • the list of staff phone numbers that get WhatsApp pings on new
 *     song requests AND event check-ins
 *
 * Reads / writes /api/venue/whatsapp-config.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, Plus, X, Loader2, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WhatsAppConfigCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [staff, setStaff] = useState([]); // [{name, phone}]
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [hostPhone, setHostPhone] = useState('');

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venue/whatsapp-config`);
      setOtpRequired(!!res.data.wa_otp_required);
      setStaff(Array.isArray(res.data.staff_whatsapp_numbers) ? res.data.staff_whatsapp_numbers : []);
      setHostPhone(res.data.host_phone || '');
    } catch (err) {
      console.warn('[WhatsApp] refresh failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveOtp = async (v) => {
    setOtpRequired(v);
    setSaving(true);
    try {
      await axios.put(`${API}/venue/whatsapp-config`, { wa_otp_required: v });
      toast.success(v ? 'WhatsApp OTP enabled' : 'WhatsApp OTP disabled');
    } catch (err) {
      console.warn('[WhatsApp] save OTP toggle failed:', err);
      setOtpRequired(!v);
      toast.error('Could not save change');
    } finally { setSaving(false); }
  };

  const saveStaff = async (next) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/venue/whatsapp-config`, {
        staff_whatsapp_numbers: next,
      });
      setStaff(res.data.staff_whatsapp_numbers || []);
    } catch (err) {
      console.warn('[WhatsApp] save staff list failed:', err);
      toast.error('Could not update staff list');
    } finally { setSaving(false); }
  };

  const handleAdd = async () => {
    const phone = newPhone.trim();
    if (!phone) { toast.error('Phone number required'); return; }
    if (staff.length >= 10) { toast.error('Maximum 10 staff numbers'); return; }
    const next = [...staff, { name: newName.trim() || 'Team', phone }];
    await saveStaff(next);
    setNewName('');
    setNewPhone('');
    toast.success('Staff number added');
  };

  const handleRemove = async (idx) => {
    const next = staff.filter((_, i) => i !== idx);
    await saveStaff(next);
  };

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl"
      data-testid="whatsapp-config-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-[#25D366]" />
          <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em]" style={{ fontFamily: 'Outfit' }}>
            WhatsApp
          </h3>
        </div>
        {saving && <Loader2 className="w-4 h-4 text-white/40 animate-spin" />}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* OTP Required */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5">
            <div className="flex-1 mr-3">
              <p className="text-white text-sm font-semibold">Verify guests with WhatsApp OTP</p>
              <p className="text-gray-500 text-[11px]">
                Guests get a 6-digit code on WhatsApp before they can request a song.
                Limits each verified guest to <span className="text-white/70">1 request</span> per night.
              </p>
            </div>
            <Switch
              checked={otpRequired}
              onCheckedChange={saveOtp}
              disabled={saving}
              className="data-[state=checked]:bg-[#25D366]"
              data-testid="wa-otp-toggle"
            />
          </div>

          {/* Staff Notification Numbers */}
          <div className="p-3 rounded-xl bg-black/30 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <Phone className="w-4 h-4 text-[#25D366]" />
              <p className="text-white text-sm font-semibold">Staff Notification Numbers</p>
            </div>
            <p className="text-gray-500 text-[11px] mb-3">
              These numbers receive WhatsApp pings for new requests and event check-ins.
              {staff.length === 0 && hostPhone && (
                <span className="block mt-1 text-yellow-400/80">
                  None set — falling back to your account phone ({hostPhone}).
                </span>
              )}
            </p>

            <div className="space-y-2 mb-3">
              {staff.map((s, i) => (
                <div
                  key={`${s.phone}-${i}`}
                  className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.03] border border-white/5"
                  data-testid={`wa-staff-row-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{s.name || 'Team'}</p>
                    <p className="text-gray-500 text-xs truncate">{s.phone}</p>
                  </div>
                  <button
                    onClick={() => handleRemove(i)}
                    disabled={saving}
                    className="p-1.5 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-500/10"
                    data-testid={`wa-staff-remove-${i}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value.slice(0, 60))}
                placeholder="Name (e.g. Floor Lead)"
                className="bg-white/[0.03] border-white/15 text-white h-10 rounded-lg text-sm flex-1"
                data-testid="wa-staff-name-input"
              />
              <Input
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value.slice(0, 20))}
                placeholder="+91…"
                className="bg-white/[0.03] border-white/15 text-white h-10 rounded-lg text-sm w-32"
                data-testid="wa-staff-phone-input"
              />
              <Button
                onClick={handleAdd}
                disabled={saving || !newPhone.trim()}
                className="h-10 bg-[#25D366] hover:bg-[#1FB855] text-black rounded-lg px-3"
                data-testid="wa-staff-add-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppConfigCard;
